type DisplayModeValue = "displayLabel" | "displayValueAndColor" | "displayLabelAndColor"

type WidgetInfoItem = {
  uuid?: string
  id?: string
  name?: string
  displayModeValue?: DisplayModeValue
  [k: string]: any
}

type ScaleItem = { key: string | number; value: number | string }

type EnumColors = Record<
  string,
  {
    backgroundColor?: string
  }
>

type FieldData = {
  uuid?: string
  nanoid?: string
  name?: string
  infoField?: {
    scale?: ScaleItem[] | null
  }
  propertiesField?: {
    enum_colors?: EnumColors
    oneOf?: Array<
      { type: "number"; minimum?: number; maximum?: number } | { type: "string"; enum: string[] }
    >
  }
}

type DisplayPiece =
  | { key: string; label: string }
  | { key: string; value: number | string; color?: string | null }
  | { key: string; label: string; color?: string | null }

type DisplayMap = Record<string, DisplayPiece>

type ResultItem = {
  uuid: string
  name?: string
  mode: DisplayModeValue
  scale: ScaleItem[]
  enumColors: EnumColors
  valueMap: DisplayMap
}

export function getScaleAndEnumColors(
  widgetInfoData: WidgetInfoItem[],
  fieldData: FieldData[]
): ResultItem[] {
  const byUuid = new Map<string, FieldData>()
  for (const f of fieldData) {
    const key = f.uuid || f.nanoid
    if (key) byUuid.set(key, f)
  }

  // récupère la liste des labels possibles (si enum string est fourni)
  const labelsFromEnum = (f?: FieldData): string[] | null => {
    const oneOf = f?.propertiesField?.oneOf
    if (!oneOf) return null
    const strEnum = oneOf.find(
      (o: any): o is { type: "string"; enum: string[] } =>
        o && o.type === "string" && Array.isArray(o.enum)
    )
    return strEnum ? strEnum.enum : null
  }

  const results: ResultItem[] = []

  for (const w of widgetInfoData) {
    if (!w.displayModeValue) continue

    const uid = w.uuid || w.id
    if (!uid) continue

    const f = byUuid.get(uid)
    if (!f) continue

    const mode = w.displayModeValue as DisplayModeValue

    const scale: ScaleItem[] = Array.isArray(f.infoField?.scale)
      ? (f.infoField!.scale as ScaleItem[])
      : []

    const enumColors: EnumColors = f.propertiesField?.enum_colors || {}

    const enumLabels = labelsFromEnum(f)

    // Construit un mapping clé -> {…} conforme au mode demandé
    const valueMap: DisplayMap = {}
    for (const s of scale) {
      const keyStr = typeof s.key === "string" ? s.key : String(s.key ?? s.value)
      const color =
        enumColors?.[keyStr]?.backgroundColor ??
        enumColors?.[String(s.value)]?.backgroundColor ??
        null

      // label = priorité aux enums string si dispo, sinon key
      const label = (enumLabels && enumLabels.includes(keyStr) && keyStr) || keyStr

      if (mode === "displayLabel") {
        valueMap[keyStr] = { key: keyStr, label }
      } else if (mode === "displayValueAndColor") {
        valueMap[keyStr] = {
          key: keyStr,
          value: s.value,
          color,
        }
      } else {
        // displayLabelAndColor
        valueMap[keyStr] = {
          key: keyStr,
          label,
          color,
        }
      }
    }

    results.push({
      uuid: uid,
      name: f.name,
      mode,
      scale,
      enumColors,
      valueMap,
    })
  }

  return results
}

export const getDefaultDataWidget = (type: string) => {
  if (type === "table" || type === "ag_table") {
    return {
      dataChart: [
        {
          first_name: "Ede",
          date_of_birth: 501092040000,
        },
        {
          first_name: "Paulette",
          date_of_birth: 331269924000,
        },
        {
          first_name: "Cullen",
          date_of_birth: 569306850000,
        },
        {
          first_name: "Jeffy",
          date_of_birth: 519914821000,
        },
        {
          first_name: "Mitchael",
          date_of_birth: 347812251000,
        },
      ],
      columnsLabelsMapping: [
        {
          label: "Prénom",
          valueId: "first_name",
          type: "string",
        },
        {
          label: "Date de naissance",
          valueId: "date_of_birth",
          type: "Timestamp",
        },
      ],
    }
  }
  if (type === "histogram") {
    return {
      dataChart: [
        {
          mois: "07/2020",
          value1: 100,
          value2: 200,
          value3: 150,
        },
        {
          mois: "08/2020",
          value1: 100,
          value2: 200,
          value3: 150,
        },
        {
          mois: "09/2020",
          value1: 100,
          value2: 200,
          value3: 150,
        },
        {
          mois: "10/2020",
          value1: 100,
          value2: 200,
          value3: 150,
        },
        {
          mois: "11/2020",
          value1: 100,
          value2: 200,
          value3: 150,
        },
      ],
      valueData: ["value1", "value2", "value3"],
      dataNames: ["Valeur 1", "Valeur 2", "Valeur 3"],
    }
  }
  if (type === "treeMap") {
    return {
      dataChart: [
        {
          valueID_kdssndfkj: "Renault",
          valueID_mkhfofgh: "Clio",
          valueID_mdzdgfgrgrdz: "Ph1",
          valueID_qwkamz: 123,
        },
        {
          valueID_kdssndfkj: "Renault",
          valueID_mkhfofgh: "Clio",
          valueID_mdzdgfgrgrdz: "Ph2",
          valueID_qwkamz: 123,
        },
        {
          valueID_kdssndfkj: "Renault",
          valueID_mkhfofgh: "Megane",
          valueID_qwkamz: 123,
        },
        {
          valueID_kdssndfkj: "Renault",
          valueID_mkhfofgh: "Espace",
          valueID_qwkamz: 123,
        },
        {
          valueID_kdssndfkj: "Peugeot",
          valueID_mkhfofgh: "206",
          valueID_qwkamz: 123,
        },
        {
          valueID_kdssndfkj: "Peugeot",
          valueID_mkhfofgh: "207",
          valueID_qwkamz: 123,
        },
        {
          valueID_kdssndfkj: "Peugeot",
          valueID_mkhfofgh: "208",
          valueID_qwkamz: 0,
        },
      ],
      valueData: ["valueID_qwkamz"],
      categoryData: ["valueID_kdssndfkj", "valueID_mkhfofgh"],
    }
  }
  if (type === "radarChart") {
    return {
      dataChart: [
        { country: "Lithuania", value1: 501, value2: 301 },
        { country: "Czech Republic", value1: 301, value2: 266 },
        { country: "Ireland", value1: 266, value2: 165 },
        { country: "Germany", value1: 165, value2: 139 },
        { country: "Australia", value1: 139, value2: 336 },
        { country: "Austria", value1: 336, value2: 290 },
        { country: "UK", value1: 290, value2: 325 },
        { country: "Belgium", value1: 325, value2: 40 },
        { country: "The Netherlands", value1: 40, value2: 430 },
      ],
      valueData: ["value1", "value2"],
      categoryData: "country",
    }
  }
  if (type === "lineChart") {
    return {
      dataChart: [
        {
          year: "2015",
          quantity_birds: 569,
          quantity_tiger: 869,
          quantity_zebra: 369,
        },
        {
          year: "2016",
          quantity_birds: 469,
          quantity_tiger: 769,
          quantity_zebra: 569,
        },
        {
          year: "2017",
          quantity_birds: 495,
          quantity_tiger: 895,
          quantity_zebra: 269,
        },
        {
          year: "2018",
          quantity_birds: 475,
          quantity_tiger: 675,
          quantity_zebra: 369,
        },
        {
          year: "2019",
          quantity_birds: 486,
          quantity_tiger: 986,
          quantity_zebra: 269,
        },
        {
          year: "2020",
          quantity_birds: 525,
          quantity_tiger: 825,
          quantity_zebra: 469,
        },
        {
          year: "2025",
          quantity_birds: 569,
          quantity_tiger: 869,
          quantity_zebra: 369,
        },
        {
          year: "2026",
          quantity_birds: 469,
          quantity_tiger: 769,
          quantity_zebra: 569,
        },
        {
          year: "2027",
          quantity_birds: 495,
          quantity_tiger: 895,
          quantity_zebra: 269,
        },
        {
          year: "2028",
          quantity_birds: 475,
          quantity_tiger: 675,
          quantity_zebra: 369,
        },
        {
          year: "2029",
          quantity_birds: 486,
          quantity_tiger: 986,
          quantity_zebra: 269,
        },
        {
          year: "2030",
          quantity_birds: 525,
          quantity_tiger: 825,
          quantity_zebra: 469,
        },
        {
          year: "2035",
          quantity_birds: 569,
          quantity_tiger: 869,
          quantity_zebra: 369,
        },
        {
          year: "2036",
          quantity_birds: 469,
          quantity_tiger: 769,
          quantity_zebra: 569,
        },
        {
          year: "2037",
          quantity_birds: 495,
          quantity_tiger: 895,
          quantity_zebra: 269,
        },
        {
          year: "2038",
          quantity_birds: 475,
          quantity_tiger: 675,
          quantity_zebra: 369,
        },
        {
          year: "2039",
          quantity_birds: 486,
          quantity_tiger: 986,
          quantity_zebra: 269,
        },
        {
          year: "2040",
          quantity_birds: 525,
          quantity_tiger: 825,
          quantity_zebra: 469,
        },
      ],
      valueData: ["quantity_birds", "quantity_tiger", "quantity_zebra"],
      categoryData: "year",
    }
  }
  if (type === "pieChart") {
    return {
      dataChart: [
        { sector: "Javascript", size: 56.6, size2: 12 },
        { sector: "Ember", size: 6, size2: 12 },
        { sector: "Node", size: 23.2, size2: 25 },
      ],
      valueData: ["size", "size2"],
      categoryData: "sector",
    }
  }
  if (type === "progressBar") {
    return {
      dataChart: [
        { value: 30, name: "value 1" },
        { value: 70, name: "value 2" },
      ],
      categoryData: "name",
      valueData: ["value"],
    }
  }
  if (type === "bubbleChart") {
    return {
      dataChart: [
        {
          name: "Afghanistan",
          x: 1349.69694102398,
          y: 60.524,
          value: 33397058,
        },
        {
          name: "Albania",
          x: 6969.30628256456,
          y: 77.185,
          value: 3227373,
        },
        {
          name: "Algeria",
          x: 6419.12782939372,
          y: 70.874,
          value: 36485828,
        },
      ],
      dataNames: ["value"],
      categoryNameId: "name",
      categoryValueId: "value",
    }
  }
  if (type === "heatMap") {
    return {
      dataChart: [
        {
          hour: "12pm",
          weekday: "Sunday",
          value: 2990,
        },
        {
          hour: "1am",
          weekday: "Sunday",
          value: 2520,
        },
        {
          hour: "2am",
          weekday: "Sunday",
          value: 2334,
        },
        {
          hour: "3am",
          weekday: "Sunday",
          value: 2230,
        },
        {
          hour: "4am",
          weekday: "Sunday",
          value: 2325,
        },
        {
          hour: "5am",
          weekday: "Sunday",
          value: 2019,
        },
        {
          hour: "6am",
          weekday: "Sunday",
          value: 2128,
        },
        {
          hour: "7am",
          weekday: "Sunday",
          value: 2246,
        },
        {
          hour: "8am",
          weekday: "Sunday",
          value: 2421,
        },
        {
          hour: "9am",
          weekday: "Sunday",
          value: 2788,
        },
        {
          hour: "10am",
          weekday: "Sunday",
          value: 2959,
        },
        {
          hour: "11am",
          weekday: "Sunday",
          value: 3018,
        },
        {
          hour: "12am",
          weekday: "Sunday",
          value: 3154,
        },
        {
          hour: "1pm",
          weekday: "Sunday",
          value: 3172,
        },
        {
          hour: "2pm",
          weekday: "Sunday",
          value: 3368,
        },
        {
          hour: "3pm",
          weekday: "Sunday",
          value: 3464,
        },
        {
          hour: "4pm",
          weekday: "Sunday",
          value: 3746,
        },
        {
          hour: "5pm",
          weekday: "Sunday",
          value: 3656,
        },
        {
          hour: "6pm",
          weekday: "Sunday",
          value: 3336,
        },
        {
          hour: "7pm",
          weekday: "Sunday",
          value: 3292,
        },
        {
          hour: "8pm",
          weekday: "Sunday",
          value: 3269,
        },
        {
          hour: "9pm",
          weekday: "Sunday",
          value: 3300,
        },
        {
          hour: "10pm",
          weekday: "Sunday",
          value: 3403,
        },
        {
          hour: "11pm",
          weekday: "Sunday",
          value: 3323,
        },
        {
          hour: "12pm",
          weekday: "Monday",
          value: 3346,
        },
        {
          hour: "1am",
          weekday: "Monday",
          value: 2725,
        },
        {
          hour: "2am",
          weekday: "Monday",
          value: 3052,
        },
        {
          hour: "3am",
          weekday: "Monday",
          value: 3876,
        },
        {
          hour: "4am",
          weekday: "Monday",
          value: 4453,
        },
        {
          hour: "5am",
          weekday: "Monday",
          value: 3972,
        },
        {
          hour: "6am",
          weekday: "Monday",
          value: 4644,
        },
        {
          hour: "7am",
          weekday: "Monday",
          value: 5715,
        },
        {
          hour: "8am",
          weekday: "Monday",
          value: 7080,
        },
        {
          hour: "9am",
          weekday: "Monday",
          value: 8022,
        },
        {
          hour: "10am",
          weekday: "Monday",
          value: 8446,
        },
        {
          hour: "11am",
          weekday: "Monday",
          value: 9313,
        },
        {
          hour: "12am",
          weekday: "Monday",
          value: 9011,
        },
        {
          hour: "1pm",
          weekday: "Monday",
          value: 8508,
        },
        {
          hour: "2pm",
          weekday: "Monday",
          value: 8515,
        },
        {
          hour: "3pm",
          weekday: "Monday",
          value: 8399,
        },
        {
          hour: "4pm",
          weekday: "Monday",
          value: 8649,
        },
        {
          hour: "5pm",
          weekday: "Monday",
          value: 7869,
        },
        {
          hour: "6pm",
          weekday: "Monday",
          value: 6933,
        },
        {
          hour: "7pm",
          weekday: "Monday",
          value: 5969,
        },
        {
          hour: "8pm",
          weekday: "Monday",
          value: 5552,
        },
        {
          hour: "9pm",
          weekday: "Monday",
          value: 5434,
        },
        {
          hour: "10pm",
          weekday: "Monday",
          value: 5070,
        },
        {
          hour: "11pm",
          weekday: "Monday",
          value: 4851,
        },
        {
          hour: "12pm",
          weekday: "Tuesday",
          value: 4468,
        },
        {
          hour: "1am",
          weekday: "Tuesday",
          value: 3306,
        },
        {
          hour: "2am",
          weekday: "Tuesday",
          value: 3906,
        },
        {
          hour: "3am",
          weekday: "Tuesday",
          value: 4413,
        },
        {
          hour: "4am",
          weekday: "Tuesday",
          value: 4726,
        },
        {
          hour: "5am",
          weekday: "Tuesday",
          value: 4584,
        },
        {
          hour: "6am",
          weekday: "Tuesday",
          value: 5717,
        },
        {
          hour: "7am",
          weekday: "Tuesday",
          value: 6504,
        },
        {
          hour: "8am",
          weekday: "Tuesday",
          value: 8104,
        },
        {
          hour: "9am",
          weekday: "Tuesday",
          value: 8813,
        },
        {
          hour: "10am",
          weekday: "Tuesday",
          value: 9278,
        },
        {
          hour: "11am",
          weekday: "Tuesday",
          value: 10425,
        },
        {
          hour: "12am",
          weekday: "Tuesday",
          value: 10137,
        },
        {
          hour: "1pm",
          weekday: "Tuesday",
          value: 9290,
        },
        {
          hour: "2pm",
          weekday: "Tuesday",
          value: 9255,
        },
        {
          hour: "3pm",
          weekday: "Tuesday",
          value: 9614,
        },
        {
          hour: "4pm",
          weekday: "Tuesday",
          value: 9713,
        },
        {
          hour: "5pm",
          weekday: "Tuesday",
          value: 9667,
        },
        {
          hour: "6pm",
          weekday: "Tuesday",
          value: 8774,
        },
        {
          hour: "7pm",
          weekday: "Tuesday",
          value: 8649,
        },
        {
          hour: "8pm",
          weekday: "Tuesday",
          value: 9937,
        },
        {
          hour: "9pm",
          weekday: "Tuesday",
          value: 10286,
        },
        {
          hour: "10pm",
          weekday: "Tuesday",
          value: 9175,
        },
        {
          hour: "11pm",
          weekday: "Tuesday",
          value: 8581,
        },
        {
          hour: "12pm",
          weekday: "Wednesday",
          value: 8145,
        },
        {
          hour: "1am",
          weekday: "Wednesday",
          value: 7177,
        },
        {
          hour: "2am",
          weekday: "Wednesday",
          value: 5657,
        },
        {
          hour: "3am",
          weekday: "Wednesday",
          value: 6802,
        },
        {
          hour: "4am",
          weekday: "Wednesday",
          value: 8159,
        },
        {
          hour: "5am",
          weekday: "Wednesday",
          value: 8449,
        },
        {
          hour: "6am",
          weekday: "Wednesday",
          value: 9453,
        },
        {
          hour: "7am",
          weekday: "Wednesday",
          value: 9947,
        },
        {
          hour: "8am",
          weekday: "Wednesday",
          value: 11471,
        },
        {
          hour: "9am",
          weekday: "Wednesday",
          value: 12492,
        },
        {
          hour: "10am",
          weekday: "Wednesday",
          value: 9388,
        },
        {
          hour: "11am",
          weekday: "Wednesday",
          value: 9928,
        },
        {
          hour: "12am",
          weekday: "Wednesday",
          value: 9644,
        },
        {
          hour: "1pm",
          weekday: "Wednesday",
          value: 9034,
        },
        {
          hour: "2pm",
          weekday: "Wednesday",
          value: 8964,
        },
        {
          hour: "3pm",
          weekday: "Wednesday",
          value: 9069,
        },
        {
          hour: "4pm",
          weekday: "Wednesday",
          value: 8898,
        },
        {
          hour: "5pm",
          weekday: "Wednesday",
          value: 8322,
        },
        {
          hour: "6pm",
          weekday: "Wednesday",
          value: 6909,
        },
        {
          hour: "7pm",
          weekday: "Wednesday",
          value: 5810,
        },
        {
          hour: "8pm",
          weekday: "Wednesday",
          value: 5151,
        },
        {
          hour: "9pm",
          weekday: "Wednesday",
          value: 4911,
        },
        {
          hour: "10pm",
          weekday: "Wednesday",
          value: 4487,
        },
        {
          hour: "11pm",
          weekday: "Wednesday",
          value: 4118,
        },
        {
          hour: "12pm",
          weekday: "Thursday",
          value: 3689,
        },
        {
          hour: "1am",
          weekday: "Thursday",
          value: 3081,
        },
        {
          hour: "2am",
          weekday: "Thursday",
          value: 6525,
        },
        {
          hour: "3am",
          weekday: "Thursday",
          value: 6228,
        },
        {
          hour: "4am",
          weekday: "Thursday",
          value: 6917,
        },
        {
          hour: "5am",
          weekday: "Thursday",
          value: 6568,
        },
        {
          hour: "6am",
          weekday: "Thursday",
          value: 6405,
        },
        {
          hour: "7am",
          weekday: "Thursday",
          value: 8106,
        },
        {
          hour: "8am",
          weekday: "Thursday",
          value: 8542,
        },
        {
          hour: "9am",
          weekday: "Thursday",
          value: 8501,
        },
        {
          hour: "10am",
          weekday: "Thursday",
          value: 8802,
        },
        {
          hour: "11am",
          weekday: "Thursday",
          value: 9420,
        },
        {
          hour: "12am",
          weekday: "Thursday",
          value: 8966,
        },
        {
          hour: "1pm",
          weekday: "Thursday",
          value: 8135,
        },
        {
          hour: "2pm",
          weekday: "Thursday",
          value: 8224,
        },
        {
          hour: "3pm",
          weekday: "Thursday",
          value: 8387,
        },
        {
          hour: "4pm",
          weekday: "Thursday",
          value: 8218,
        },
        {
          hour: "5pm",
          weekday: "Thursday",
          value: 7641,
        },
        {
          hour: "6pm",
          weekday: "Thursday",
          value: 6469,
        },
        {
          hour: "7pm",
          weekday: "Thursday",
          value: 5441,
        },
        {
          hour: "8pm",
          weekday: "Thursday",
          value: 4952,
        },
        {
          hour: "9pm",
          weekday: "Thursday",
          value: 4643,
        },
        {
          hour: "10pm",
          weekday: "Thursday",
          value: 4393,
        },
        {
          hour: "11pm",
          weekday: "Thursday",
          value: 4017,
        },
        {
          hour: "12pm",
          weekday: "Friday",
          value: 4022,
        },
        {
          hour: "1am",
          weekday: "Friday",
          value: 3063,
        },
        {
          hour: "2am",
          weekday: "Friday",
          value: 3638,
        },
        {
          hour: "3am",
          weekday: "Friday",
          value: 3968,
        },
        {
          hour: "4am",
          weekday: "Friday",
          value: 4070,
        },
        {
          hour: "5am",
          weekday: "Friday",
          value: 4019,
        },
        {
          hour: "6am",
          weekday: "Friday",
          value: 4548,
        },
        {
          hour: "7am",
          weekday: "Friday",
          value: 5465,
        },
        {
          hour: "8am",
          weekday: "Friday",
          value: 6909,
        },
        {
          hour: "9am",
          weekday: "Friday",
          value: 7706,
        },
        {
          hour: "10am",
          weekday: "Friday",
          value: 7867,
        },
        {
          hour: "11am",
          weekday: "Friday",
          value: 8615,
        },
        {
          hour: "12am",
          weekday: "Friday",
          value: 8218,
        },
        {
          hour: "1pm",
          weekday: "Friday",
          value: 7604,
        },
        {
          hour: "2pm",
          weekday: "Friday",
          value: 7429,
        },
        {
          hour: "3pm",
          weekday: "Friday",
          value: 7488,
        },
        {
          hour: "4pm",
          weekday: "Friday",
          value: 7493,
        },
        {
          hour: "5pm",
          weekday: "Friday",
          value: 6998,
        },
        {
          hour: "6pm",
          weekday: "Friday",
          value: 5941,
        },
        {
          hour: "7pm",
          weekday: "Friday",
          value: 5068,
        },
        {
          hour: "8pm",
          weekday: "Friday",
          value: 4636,
        },
        {
          hour: "9pm",
          weekday: "Friday",
          value: 4241,
        },
        {
          hour: "10pm",
          weekday: "Friday",
          value: 3858,
        },
        {
          hour: "11pm",
          weekday: "Friday",
          value: 3833,
        },
        {
          hour: "12pm",
          weekday: "Saturday",
          value: 3503,
        },
        {
          hour: "1am",
          weekday: "Saturday",
          value: 2842,
        },
        {
          hour: "2am",
          weekday: "Saturday",
          value: 2808,
        },
        {
          hour: "3am",
          weekday: "Saturday",
          value: 2399,
        },
        {
          hour: "4am",
          weekday: "Saturday",
          value: 2280,
        },
        {
          hour: "5am",
          weekday: "Saturday",
          value: 2139,
        },
        {
          hour: "6am",
          weekday: "Saturday",
          value: 2527,
        },
        {
          hour: "7am",
          weekday: "Saturday",
          value: 2940,
        },
        {
          hour: "8am",
          weekday: "Saturday",
          value: 3066,
        },
        {
          hour: "9am",
          weekday: "Saturday",
          value: 3494,
        },
        {
          hour: "10am",
          weekday: "Saturday",
          value: 3287,
        },
        {
          hour: "11am",
          weekday: "Saturday",
          value: 3416,
        },
        {
          hour: "12am",
          weekday: "Saturday",
          value: 3432,
        },
        {
          hour: "1pm",
          weekday: "Saturday",
          value: 3523,
        },
        {
          hour: "2pm",
          weekday: "Saturday",
          value: 3542,
        },
        {
          hour: "3pm",
          weekday: "Saturday",
          value: 3347,
        },
        {
          hour: "4pm",
          weekday: "Saturday",
          value: 3292,
        },
        {
          hour: "5pm",
          weekday: "Saturday",
          value: 3416,
        },
        {
          hour: "6pm",
          weekday: "Saturday",
          value: 3131,
        },
        {
          hour: "7pm",
          weekday: "Saturday",
          value: 3057,
        },
        {
          hour: "8pm",
          weekday: "Saturday",
          value: 3227,
        },
        {
          hour: "9pm",
          weekday: "Saturday",
          value: 3060,
        },
        {
          hour: "10pm",
          weekday: "Saturday",
          value: 2855,
        },
        {
          hour: "11pm",
          weekday: "Saturday",
          value: 2625,
        },
      ],
      categoryValueId: "value",
      categoryXId: "hour",
      categoryYId: "weekday",
    }
  }
  if (type === "heatMapRisk") {
    const colors = {
      critical: "#ca0101",
      bad: "#e17a2d",
      medium: "#e1d92d",
      good: "#5dbe24",
      verygood: "#0b7d03",
    }
    return {
      dataChart: [
        { y: "Critical", x: "Very good", columnSettings: { fill: colors.medium }, value: 20 },
        { y: "Bad", x: "Very good", columnSettings: { fill: colors.good }, value: 15 },
        { y: "Medium", x: "Very good", columnSettings: { fill: colors.verygood }, value: 25 },
        { y: "Good", x: "Very good", columnSettings: { fill: colors.verygood }, value: 15 },
        { y: "Very good", x: "Very good", columnSettings: { fill: colors.verygood }, value: 12 },
        { y: "Critical", x: "Good", columnSettings: { fill: colors.bad }, value: 30 },
        { y: "Bad", x: "Good", columnSettings: { fill: colors.medium }, value: 24 },
        { y: "Medium", x: "Good", columnSettings: { fill: colors.good }, value: 25 },
        { y: "Good", x: "Good", columnSettings: { fill: colors.verygood }, value: 15 },
        { y: "Very good", x: "Good", columnSettings: { fill: colors.verygood }, value: 25 },
        { y: "Critical", x: "Medium", columnSettings: { fill: colors.bad }, value: 33 },
        { y: "Bad", x: "Medium", columnSettings: { fill: colors.bad }, value: 14 },
        { y: "Medium", x: "Medium", columnSettings: { fill: colors.medium }, value: 20 },
        { y: "Good", x: "Medium", columnSettings: { fill: colors.good }, value: 19 },
        { y: "Very good", x: "Medium", columnSettings: { fill: colors.good }, value: 25 },
        { y: "Critical", x: "Bad", columnSettings: { fill: colors.critical }, value: 31 },
        { y: "Bad", x: "Bad", columnSettings: { fill: colors.critical }, value: 24 },
        { y: "Medium", x: "Bad", columnSettings: { fill: colors.bad }, value: 25 },
        { y: "Good", x: "Bad", columnSettings: { fill: colors.medium }, value: 15 },
        { y: "Very good", x: "Bad", columnSettings: { fill: colors.good }, value: 15 },
      ],
      xAxisValue: [
        { category: "Critical" },
        { category: "Bad" },
        { category: "Medium" },
        { category: "Good" },
        { category: "Very good" },
      ],
      yAxisValue: [
        { category: "Critical" },
        { category: "Bad" },
        { category: "Medium" },
        { category: "Good" },
        { category: "Very good" },
      ],
    }
  }
  if (type === "cloudWords") {
    return {
      dataChart: [
        { tag: "JavaScript", weight: 64.96 },
        { tag: "HTML/CSS", weight: 56.07 },
        { tag: "Python", weight: 48.24 },
        { tag: "SQL", weight: 47.08 },
        { tag: "Java", weight: 35.35 },
        { tag: "Node.js", weight: 33.91 },
        { tag: "TypeScript", weight: 30.19 },
        { tag: "C#", weight: 27.86 },
        { tag: "Bash/Shell", weight: 27.13 },
        { tag: "C++", weight: 24.31 },
        { tag: "PHP", weight: 21.98 },
        { tag: "C", weight: 21.01 },
        { tag: "PowerShell", weight: 10.75 },
        { tag: "Go", weight: 9.55 },
        { tag: "Kotlin", weight: 8.32 },
        { tag: "Rust", weight: 7.03 },
        { tag: "Ruby", weight: 6.75 },
        { tag: "Dart", weight: 6.02 },
      ],
      valueData: "weight",
      categoryData: "tag",
    }
  }
  if (type === "gaugeChart") {
    return {
      dataChart: [
        {
          name: "My First Value",
          value: 40,
        },
        {
          name: "My Second Value",
          value: 60,
        },
      ],
      valueData: ["value"],
      dataNames: ["My First Value", "My Second Value"],
    }
  }
  if (type === "multiGauge") {
    return {
      dataChart: [
        { country: "Lithuania", value1: 501 },
        { country: "Czech Republic", value1: 301 },
        { country: "Ireland", value1: 266 },
        { country: "Germany", value1: 165 },
        { country: "Australia", value1: 139 },
        { country: "Austria", value1: 336 },
        { country: "UK", value1: 290 },
        { country: "Belgium", value1: 325 },
        { country: "The Netherlands", value1: 40 },
      ],
      valueData: ["value1"],
      dataNames: ["value1"],
      categoryData: "country",
    }
  }
  if (type === "fillingGauge") {
    return {
      dataChart: [
        {
          value: 1,
          category: "Un",
        },
        {
          value: 2,
          category: "Deux",
        },
        {
          value: 3,
          category: "Trois",
        },
        {
          value: 4,
          category: "Quatre",
        },
        {
          value: 5,
          category: "Cinq",
        },
        {
          value: 6,
          category: "Six",
        },
        {
          value: 7,
          category: "Sept",
        },
      ],
      valueData: ["value"],
      categoryData: "category",
    }
  }
  if (type === "horizontalBarChart") {
    return {
      dataChart: [
        {
          topicName: "Research",
          myValue1: 450,
          myValue2: 500,
        },
        {
          topicName: "Marketing",
          myValue1: 1200,
          myValue2: 830,
        },
        {
          topicName: "Distribution",
          myValue1: 1850,
          myValue2: 200,
        },
      ],
      valueData: ["myValue1", "myValue2"],
      dataNames: ["My Value 1", "My Value 2"],
      categoryData: "topicName",
    }
  }

  if (type === "pipeline") {
    return {
      valueData: ["111111"],
      categoryData: "2222222",
      pipelineColumns: ["colonne 1", "colonne 2", "colonne 3", "colonne 4"],
      dataChart: [
        {
          111111: "projet 1; projet 2; projet 3",
          2222222: "colonne 1",
        },
        {
          111111: "projet 4; projet 5; projet 6",
          2222222: "colonne 2",
        },
        {
          111111: "projet 7",
          2222222: "colonne 3",
        },
        {
          111111: "projet 9; projet 10; projet 11",
          2222222: "colonne 4",
        },
      ],
    }
  }
  return {
    dataChart: [],
  }
}
